import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getBillingRefusal, type BillingRefusal } from "@onetasklist/shared";
import { useApi } from "@api/useApi";
import { useWizard } from "@contexts/WizardContext";
import { useBoardCreationWizardText } from "./BoardCreationWizard.i18n";
import { BoardCreationWizardViewModel, WizardStep } from "./BoardCreationWizard.ViewModel.types";

export const useBoardCreationWizardViewModel = (): BoardCreationWizardViewModel => {
  const api = useApi();
  const navigate = useNavigate();
  const { isWizardOpen, closeWizard } = useWizard();
  const { t } = useTranslation();
  const text = useBoardCreationWizardText(t);

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [wizardBoardId, setWizardBoardId] = useState<string | null>(null);
  const [isCreatingBoard, setIsCreatingBoard] = useState(isWizardOpen);
  const [billingRefusal, setBillingRefusal] = useState<BillingRefusal | null>(null);
  const hasCreatedRef = useRef(false);

  useEffect(() => {
    if (!isWizardOpen) return;
    if (hasCreatedRef.current) return;
    hasCreatedRef.current = true;

    let cancelled = false;
    setIsCreatingBoard(true);

    api.boards
      .createBoard({ name: "My Board" })
      .then((board) => {
        if (!cancelled) setWizardBoardId(board.id);
      })
      .catch((error: unknown) => {
        // Over the plan's board limit, offer an upgrade. Otherwise the wizard still opens so the user can
        // retry or exit.
        if (!cancelled) setBillingRefusal(getBillingRefusal(error));
      })
      .finally(() => {
        if (!cancelled) setIsCreatingBoard(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isWizardOpen, api.boards]);

  useEffect(() => {
    if (!isWizardOpen) {
      setCurrentStep(1);
      setWizardBoardId(null);
      setIsCreatingBoard(false);
      setBillingRefusal(null);
      hasCreatedRef.current = false;
    }
  }, [isWizardOpen]);

  const dismissBillingRefusal = useCallback((): void => {
    setBillingRefusal(null);
    closeWizard();
  }, [closeWizard]);

  const goToStep = useCallback((step: WizardStep): void => {
    setCurrentStep(step);
  }, []);

  const saveAndExit = useCallback(async (): Promise<void> => {
    if (wizardBoardId) {
      navigate(`/${wizardBoardId}/tasks`);
    }
    closeWizard();
  }, [closeWizard, navigate, wizardBoardId]);

  const finishWizard = useCallback(async (): Promise<void> => {
    if (wizardBoardId) {
      navigate(`/${wizardBoardId}/tasks`);
    }
    closeWizard();
  }, [closeWizard, navigate, wizardBoardId]);

  return {
    currentStep,
    wizardBoardId,
    isCreatingBoard,
    billingRefusal,
    dismissBillingRefusal,
    goToStep,
    saveAndExit,
    finishWizard,
    titleText: text.titleText,
    saveAndExitText: text.saveAndExitText,
    continueText: text.continueText,
    finishText: text.finishText,
    backText: text.backText,
    step1LabelText: text.step1LabelText,
    step2LabelText: text.step2LabelText,
    step3LabelText: text.step3LabelText,
  };
};
